import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import {
  useMutation,
  useQuery,
  useRunInvalidationForMutation
} from '@sequencehq/api/utils'
import { useNotifications } from '@sequencehq/core-components'
import { MINUTES, SECONDS } from '@sequencehq/utils/dates'
import { getIntegrationName } from 'Integrations/utils/getIntegrationName'
import { buildSalesOrderRows } from './netsuiteSalesOrders.helpers'
import { getSyncErrorDescription } from 'Integrations/integrationsConfig/common/shared'

const SERVICE = 'NetSuite'

/**
 * The push only writes the link asynchronously, so the URL isn't on the
 * schedule yet when the mutation resolves.
 */
const LINK_PROPAGATION_DELAY = 2 * SECONDS

export const useNetsuiteSalesOrdersWidget = (props: {
  billingScheduleId: string
  disabled?: boolean
}) => {
  const serviceName = getIntegrationName(SERVICE)
  const { displayNotification } = useNotifications()
  const runMutationInvalidation = useRunInvalidationForMutation()

  const billingScheduleQuery = useQuery(
    dashboardv99990101Client.getBillingSchedule,
    { id: props.billingScheduleId },
    {
      // Shared with the other integration widgets in this inspector.
      staleTime: 1 * MINUTES,
      select: billingSchedule => {
        if (!billingSchedule) {
          return null
        }

        return {
          customerId: billingSchedule.customerId,
          isActive: billingSchedule.status === 'ACTIVE',
          rows: buildSalesOrderRows(billingSchedule.phases)
        }
      }
    }
  )

  const customerQuery = useQuery(
    dashboardv99990101Client.getCustomer,
    { id: billingScheduleQuery.data?.customerId ?? '' },
    {
      enabled: Boolean(billingScheduleQuery.data?.customerId),
      staleTime: 1 * MINUTES,
      select: customer =>
        customer?.integrationIds.find(
          integrationId => integrationId.service === SERVICE
        ) ?? null
    }
  )

  const syncMutation = useMutation(
    dashboardv99990101Client.postPushBillingScheduleToExternalService
  )

  const syncBillingSchedule = () => {
    syncMutation.mutate(
      {
        billingScheduleId: props.billingScheduleId,
        service: SERVICE
      },
      {
        onError: error => {
          displayNotification(
            `Failed to sync billing schedule to ${serviceName}`,
            { type: 'error', description: getSyncErrorDescription(error) }
          )
        },
        onSuccess: () => {
          displayNotification(`Billing schedule synced to ${serviceName}`, {
            type: 'success'
          })
          setTimeout(() => {
            runMutationInvalidation(
              dashboardv99990101Client.postPushBillingScheduleToExternalService
            )
          }, LINK_PROPAGATION_DELAY)
        }
      }
    )
  }

  const rows = billingScheduleQuery.data?.rows ?? []
  const hasSalesOrders = rows.some(row => Boolean(row.salesOrder))

  return {
    isLoading: billingScheduleQuery.isPending,
    serviceName,
    isActive: Boolean(billingScheduleQuery.data?.isActive),
    rows,
    hasSalesOrders,
    sync: {
      onClick: syncBillingSchedule,
      isSyncing: syncMutation.isPending,
      disabled:
        Boolean(props.disabled) ||
        !billingScheduleQuery.data?.isActive ||
        !customerQuery.data ||
        syncMutation.isPending
    }
  }
}
